import React, { useEffect, useState } from 'react';

const SITE_URL = 'https://summitproduction.co';
// Summit's own colours: ink on an off-white ground, and the four bars over the wordmark
const INK = '#0E0E0E';
const PAPER = '#FAFAF9';
const BARS = ['#FF6B35', '#1A6FB5', '#1A6FB5', '#FF6B35', '#F2B705', '#9AA0A6'];

const useIsMobile = (bp = 768) => {
  const [m, setM] = useState(typeof window !== 'undefined' ? window.innerWidth < bp : false);
  useEffect(() => {
    const on = () => setM(window.innerWidth < bp);
    on(); window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, [bp]);
  return m;
};

const Bars = ({ height = 6 }) => (
  <div style={{ display: 'flex', gap: '6px', width: '100%' }}>
    {BARS.map((c, i) => <span key={i} style={{ flex: i % 3 === 0 ? 2 : 1, height, background: c, borderRadius: 1 }} />)}
  </div>
);

const MetaItem = ({ label, value }) => (
  <div style={{ minWidth: 0 }}>
    <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.12em', color: 'var(--text-secondary)', marginBottom: '7px' }}>{label}</div>
    <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-primary)' }}>{value}</div>
  </div>
);

const Hero = () => (
  <div>
    <div style={{
      background: PAPER, borderRadius: '18px', padding: 'clamp(40px, 8vw, 80px) clamp(24px, 6vw, 72px)',
      marginBottom: 'clamp(28px, 4vw, 40px)', border: '1px solid rgba(14,14,14,0.08)', color: INK
    }}>
      <div style={{ maxWidth: '520px' }}>
        <img src="/summit/wordmark.png" alt="SUMMIT®" style={{ width: '100%', height: 'auto', display: 'block' }} />
        <div style={{ marginTop: '14px', fontSize: '11px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: '#5a5a5a' }}>
          Saudi production event management
        </div>
      </div>
      <p dir="rtl" lang="ar" style={{ textAlign: 'left', margin: 'clamp(22px, 4vw, 34px) 0 0', fontSize: 'clamp(1.05rem, 2.4vw, 1.35rem)', fontWeight: 600 }}>
        شركة قمة السعودية لتنظيم الفعاليات والإنتاج
      </p>
    </div>
    <span style={{ display: 'block', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#FF6B35', marginBottom: '10px' }}>
      Events
    </span>
    <h1 style={{ margin: 0, fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 800, letterSpacing: '-0.03em', color: 'var(--text-primary)' }}>Summit</h1>
    <p style={{ margin: '16px 0 0', maxWidth: '60ch', fontSize: '1.05rem', lineHeight: 1.7, color: 'var(--text-primary)' }}>
      A Saudi company that produces events in sports, entertainment and culture, based in Riyadh.
      The brand and the website are mine, from the wordmark and its colour bars to the site in Arabic and English.
    </p>
    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', margin: '24px 0 32px' }}>
      <a href={SITE_URL} target="_blank" rel="noopener noreferrer" style={{
        padding: '12px 20px', borderRadius: '12px', background: '#FF6B35', color: '#fff', fontWeight: 700, fontSize: '14px', textDecoration: 'none'
      }}>Visit summitproduction.co →</a>
    </div>
    <div style={{
      // flex, not grid: responsive.css forces every inline "fr" grid to one column on phones
      display: 'flex', justifyContent: 'space-between', gap: 'clamp(8px, 3vw, 30px)', paddingTop: '24px', borderTop: '1px solid var(--border-color)'
    }}>
      <MetaItem label="Category" value="Events" />
      <MetaItem label="My work" value="Brand & website" />
      <MetaItem label="Based in" value="Riyadh" />
      <MetaItem label="Status" value="Live" />
    </div>
  </div>
);

/* The real site, live: in a browser frame and on a phone, side by side. */
const LiveSite = () => {
  const mobile = useIsMobile();
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', alignItems: 'flex-start' }}>
      <div style={{ flex: '1 1 520px', minWidth: 0, borderRadius: '18px', overflow: 'hidden', border: '1px solid var(--border-color)', boxShadow: '0 20px 50px rgba(0,0,0,0.18)', background: '#fff' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '11px 14px', background: PAPER, borderBottom: '1px solid rgba(14,14,14,0.08)' }}>
          {['#FF6B35', '#F2B705', '#1A6FB5'].map((c) => <span key={c} style={{ width: 11, height: 11, borderRadius: '50%', background: c }} />)}
          <span style={{ flex: 1, textAlign: 'center', fontSize: '12px', color: '#444', fontWeight: 600 }}>summitproduction.co</span>
          <a href={SITE_URL} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#444', fontWeight: 600 }}>Open ↗</a>
        </div>
        <iframe src={SITE_URL} title="summitproduction.co, live" loading="lazy"
          style={{ display: 'block', width: '100%', height: mobile ? '520px' : '660px', border: 0, background: PAPER }} />
      </div>
      {!mobile && (
        <div style={{ flex: '0 0 300px', margin: '0 auto', borderRadius: '34px', padding: '10px', background: INK, boxShadow: '0 20px 50px rgba(0,0,0,0.3)' }}>
          <iframe src={SITE_URL} title="summitproduction.co on a phone, live" loading="lazy"
            style={{ display: 'block', width: '100%', height: '600px', border: 0, borderRadius: '25px', background: PAPER }} />
        </div>
      )}
    </div>
  );
};

/* The site's own sections: photographs with Summit's type on them. Swipe sideways. */
const LOOK = [1, 2, 3, 4, 5, 6, 7, 8];
const Look = () => (
  <div style={{ display: 'flex', gap: '14px', overflowX: 'auto', scrollSnapType: 'x mandatory', paddingBottom: '10px', scrollbarWidth: 'none' }}>
    {LOOK.map((n) => (
      <img key={n} src={`/summit/look/${n}.webp`} alt="" loading="lazy" style={{
        flex: '0 0 auto', width: 'min(62vw, 240px)', aspectRatio: '390 / 844', objectFit: 'cover',
        borderRadius: '18px', scrollSnapAlign: 'start', boxShadow: '0 12px 30px rgba(0,0,0,0.25)'
      }} />
    ))}
  </div>
);

const Mark = () => (
  <div style={{
    background: PAPER, borderRadius: '18px', padding: 'clamp(32px, 6vw, 56px) 24px', border: '1px solid rgba(14,14,14,0.08)',
    display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 'clamp(24px, 6vw, 72px)'
  }}>
    <img src="/summit/mark.png" alt="The Summit mark" style={{ width: 'min(42%, 220px)', height: 'auto' }} />
    <div style={{ width: 'min(100%, 360px)' }}>
      <Bars height={10} />
      <div style={{ marginTop: '16px', fontSize: '13px', color: '#555', lineHeight: 1.6 }}>
        The colour bars that sit above the wordmark, and the mark built from the same shapes.
      </div>
    </div>
  </div>
);

const End = () => (
  <div style={{
    borderRadius: '28px', padding: 'clamp(44px, 8vw, 72px) 24px', textAlign: 'center', background: PAPER, color: INK,
    border: '1px solid rgba(14,14,14,0.1)', boxShadow: '0 22px 50px rgba(0,0,0,0.18)'
  }}>
    <img src="/summit/wordmark.png" alt="SUMMIT®" style={{ width: 'min(70%, 300px)', height: 'auto' }} />
    <div style={{ fontSize: '15px', marginTop: '14px', color: '#444' }}>We turn spaces into experience.</div>
    <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '24px' }}>
      <a href={SITE_URL} target="_blank" rel="noopener noreferrer" style={{
        padding: '12px 20px', borderRadius: '12px', background: '#FF6B35', color: '#fff', fontWeight: 700, fontSize: '14px', textDecoration: 'none'
      }}>Visit summitproduction.co</a>
      <a href="/" style={{
        padding: '12px 20px', borderRadius: '12px', background: 'transparent', color: INK, fontWeight: 600, fontSize: '14px',
        textDecoration: 'none', border: '1px solid rgba(14,14,14,0.3)'
      }}>See more work ↗</a>
    </div>
  </div>
);

export const summitContent = {
  breadcrumb: { text: 'Back to Home', url: '/' },
  sections: [
    { id: 'hero', spacing: 'clamp(48px, 7vw, 80px)', blocks: [{ type: 'custom', render: () => <Hero /> }] },
    { id: 'live', title: 'The website, live', blocks: [{ type: 'custom', render: () => <LiveSite /> }] },
    { id: 'mark', title: 'The mark', blocks: [{ type: 'custom', render: () => <Mark /> }] },
    { id: 'look', title: 'The look', blocks: [{ type: 'custom', render: () => <Look /> }] },
    { id: 'end', blocks: [{ type: 'custom', render: () => <End /> }] }
  ]
};
